/**
 * apps/web :: lib/auth-api
 *
 * Typed wrappers for the custom-UI WorkOS auth endpoints. Each function is a
 * thin `apiFetch` call — no state, no side-effects. Callers own navigation and
 * context refresh after a successful sign-in.
 */
import { apiFetch } from "@/lib/api";

export interface SignUpResult {
  /** "ok" when the user was already verified and a session was created. */
  status: "ok" | "verification_sent";
  email?: string;
  /**
   * Populated when `status === "verification_sent"`. The SPA holds this in
   * memory and hands it back to `resendVerification()` if the user asks
   * for a fresh code. ROADS-504.
   */
  user_id?: string;
  /**
   * ROADS-527 — populated when `status === "verification_sent"`. WorkOS
   * pairs this token + the 6-digit code server-side to complete email
   * verification, so the SPA MUST hold it in memory across the signup →
   * verify step and pass it to `verifyEmail()`. Token has a short TTL
   * (~10 min); after that the user has to re-trigger signup.
   */
  pending_authentication_token?: string;
}

/**
 * Create a new account via `POST /auth/signup`.
 *
 * Returns `{ status: "verification_sent", email }` when WorkOS sends a
 * verification email (the normal path). Returns `{ status: "ok" }` in the
 * rare case the user was already verified (admin-created accounts).
 * Throws {@link ApiError} on validation or duplicate-email errors.
 */
export async function signUp(
  email: string,
  password: string,
  firstName?: string,
  lastName?: string,
): Promise<SignUpResult> {
  return apiFetch<SignUpResult>("/auth/signup", {
    method: "POST",
    body: JSON.stringify({
      email,
      password,
      ...(firstName ? { first_name: firstName } : {}),
      ...(lastName ? { last_name: lastName } : {}),
    }),
  });
}

/**
 * Sign in via `POST /auth/signin`. Sets the session cookie on success.
 * Throws {@link ApiError} with `status=401` on invalid credentials or
 * `status=403` when the user's email is not yet verified.
 */
export async function signIn(email: string, password: string): Promise<void> {
  await apiFetch("/auth/signin", {
    method: "POST",
    body: JSON.stringify({ email, password }),
  });
}

/**
 * Confirm an email verification code via `POST /auth/verify-email`.
 *
 * Submits the 6-digit OTP the user pasted from the WorkOS-sent
 * verification email, paired with the `pending_authentication_token`
 * issued by the prior `signUp()` call (ROADS-527). On success the
 * backend mints a session and the caller should refresh `AuthContext`
 * + navigate.
 *
 * Throws {@link ApiError} with `status=400` when WorkOS rejects the
 * pairing (wrong code, expired token, already-used code, etc.).
 */
export async function verifyEmail(
  code: string,
  pendingAuthenticationToken: string,
): Promise<void> {
  await apiFetch("/auth/verify-email", {
    method: "POST",
    body: JSON.stringify({
      code,
      pending_authentication_token: pendingAuthenticationToken,
    }),
  });
}

/**
 * Resend a verification email via `POST /auth/resend-verification`.
 *
 * Takes the `user_id` returned by the prior {@link signUp} call. Always
 * resolves with 200 (backend swallows WorkOS errors to avoid surfacing
 * upstream hiccups during the resend race). Caller should still wrap in
 * a client-side cooldown timer (default 60s) so users can't spam the
 * button. ROADS-504.
 */
export async function resendVerification(userId: string): Promise<void> {
  await apiFetch("/auth/resend-verification", {
    method: "POST",
    body: JSON.stringify({ user_id: userId }),
  });
}

/**
 * Send a password-reset email via `POST /auth/forgot-password`.
 * Always resolves — the backend swallows WorkOS errors to prevent email
 * enumeration. Callers should always show "if the address is registered,
 * you'll get an email" copy.
 */
export async function forgotPassword(email: string): Promise<void> {
  await apiFetch("/auth/forgot-password", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

/**
 * Confirm a password reset via `POST /auth/reset-password`.
 * Throws {@link ApiError} with `status=400` when the token is expired or
 * already used, or if the new password violates WorkOS policy.
 */
export async function resetPassword(token: string, newPassword: string): Promise<void> {
  await apiFetch("/auth/reset-password", {
    method: "POST",
    body: JSON.stringify({ token, new_password: newPassword }),
  });
}

/**
 * A single OAuth identity (Google, Apple, ...) attached to the user's
 * WorkOS account. ROADS-511.
 *
 * `provider` is the wire-form string ("GoogleOAuth", "AppleOAuth", ...);
 * map it to user-facing copy at the call site. `type` is always
 * `"OAuth"` today but kept loose for forward-compat with future WorkOS
 * additions (SAML, OIDC, etc.).
 */
export interface UserIdentity {
  provider: string;
  type: string;
}

export interface UserIdentitiesResponse {
  identities: UserIdentity[];
}

/**
 * List the OAuth identities attached to the signed-in user via
 * `GET /auth/me/identities`. Returns an empty list when the user signed
 * up with email + password directly.
 *
 * Note: WorkOS does NOT (yet) expose an unlink endpoint, so this is
 * read-only. The UI surfaces the providers + nudges users to also set
 * up email/password via the existing forgot-password flow rather than
 * offering an Unlink button that can't actually unlink (ROADS-511).
 *
 * Throws {@link ApiError} on 401 (unauthenticated) or 502 (WorkOS
 * upstream error).
 */
export async function listIdentities(): Promise<UserIdentity[]> {
  const body = await apiFetch<UserIdentitiesResponse>("/auth/me/identities");
  return body.identities;
}
